import {
    SearchableAnalysisInDecision,
    ModelType,
    SearchableDecisionInstantSearch,
} from '@livv/models';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Accordion, AccordionSummary, Grid } from '@mui/material';
import { Hit } from 'instantsearch.js';
import useTranslation from 'next-translate/useTranslation';
import { FC, MouseEvent, SyntheticEvent, useMemo, useState } from 'react';
import ModelTypeLogo from '../ContentLogo/ModelTypeLogo';
import AnalysisList from './AnalysisList';
import useAmplitude from '@utils/hooks/useAmplitude';
import useSnippetAnalysis, { HitWithSnippetResult } from '@utils/hooks/useSnippetAnalysis';
import { extractSpecificFilter } from '@utils/search/previews';
import { TrackingEventName } from '@utils/types/amplitude/events';

interface DecisionAnalysedProps {
    filters?: string;
    hit: SearchableDecisionInstantSearch;
    query?: string;
}

const accordionSx = {
    '&.Mui-expanded': {
        margin: 0,
    },
    '&::before': {
        content: 'none',
    },
    alignItems: 'start',
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    marginTop: 2,
};

const accordionSummarySx = {
    '& .MuiAccordionSummary-content': {
        '&.Mui-expanded': {
            marginTop: 2,
        },
        justifyContent: 'flex-end',
        margin: 0,
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
        '&.Mui-expanded': {
            marginTop: 2,
        },
        color: 'primary.main',
    },
    border: 0,
    color: 'primary.main',
    fontSize: 14,
    fontWeight: 'medium',
    minHeight: 32,
    order: 2,
    padding: 0.25,
};

/**
 * Function to replace initial analyses by analyses matching with thematic plan filter
 * Return inital Hit if no filter
 */
const getHitWithRelevantAnalyses = (
    initialHit: SearchableDecisionInstantSearch,
    analyses: SearchableAnalysisInDecision[],
    pathNodeIdsFilter: string[],
): SearchableDecisionInstantSearch => {
    if (analyses?.length && pathNodeIdsFilter.length > 0) {
        const analysesMatchingWithNodeIdsFilter: SearchableAnalysisInDecision[] = [];
        const analysesIndexToKeep: number[] = [];

        // we keep only analyses belongs to pathNode filter
        analyses?.forEach((analysis, index) => {
            if (pathNodeIdsFilter.every((v) => analysis?.pathNodeIds?.includes(v))) {
                analysesMatchingWithNodeIdsFilter.push(analysis);
                analysesIndexToKeep.push(index);
            }
        });

        const initialSnippetAnalyses =
            // eslint-disable-next-line no-underscore-dangle
            initialHit._snippetResult as HitWithSnippetResult['_snippetResult'];

        const initialHighLightAnalyses =
            // eslint-disable-next-line no-underscore-dangle
            initialHit._highlightResult as HitWithSnippetResult['_highlightResult'];

        const relevantSnippetAnalyses = initialSnippetAnalyses?.analyses?.filter((_, index) =>
            analysesIndexToKeep.includes(index),
        );

        const relevantHighLightAnalyses = initialHighLightAnalyses?.analyses?.filter((_, index) =>
            analysesIndexToKeep.includes(index),
        );

        const hitWithFilteredAnalyses = {
            ...initialHit,
            _highlightResult: {
                // eslint-disable-next-line no-underscore-dangle
                ...initialHighLightAnalyses,
                analyses: relevantHighLightAnalyses ?? [],
            },
            _snippetResult: {
                // eslint-disable-next-line no-underscore-dangle
                ...initialSnippetAnalyses,
                analyses: relevantSnippetAnalyses ?? [],
            },
            analyses: analysesMatchingWithNodeIdsFilter,
        };

        return hitWithFilteredAnalyses;
    }

    return initialHit;
};

const DecisionAnalysed: FC<DecisionAnalysedProps> = ({ filters, hit: initialHit, query }) => {
    const { t } = useTranslation('search');
    const [isExpanded, setIsExpanded] = useState(false);
    const { analyses, baseObjectID: decisionId } = useMemo(() => initialHit, [initialHit]);

    const hit = useMemo(() => {
        const regex = /analyses\.pathNodeIds:(?:'|")(\w+)(?:'|")/g;
        const pathNodeIdsFilter: string[] = extractSpecificFilter(filters || '', regex);

        return getHitWithRelevantAnalyses(initialHit, analyses, pathNodeIdsFilter);
    }, [initialHit, analyses, filters]);

    const { track } = useAmplitude();

    const { hitWithFirstTwoAnalyses, hitAnalysisForDropdown, snippetAnalysesHasMatch } =
        useSnippetAnalysis(hit as Hit<SearchableDecisionInstantSearch> & HitWithSnippetResult);

    if (!hit?.analyses || hit?.analyses.length === 0) {
        return null;
    }
    const handleAccordionChange = (event: SyntheticEvent, expanded: boolean) => {
        setIsExpanded(expanded);
    };

    const handleAccordionClick = (event: MouseEvent<HTMLDivElement>) => {
        // Disabling the link behavior of the global card that wraps the accordion
        event.preventDefault();
        track({
            name: TrackingEventName.ANALYSIS_RESULT_SHOW_MORE_CLICKED,
            params: {
                decision_id: decisionId,
            },
        });
    };

    return (
        <>
            <Grid sx={{ alignItems: 'left', display: 'flex', mt: 2.5 }} item>
                <ModelTypeLogo
                    label={t('decision.accordion.title')}
                    modelType={ModelType.ANALYSIS}
                />
            </Grid>
            <AnalysisList
                hasFullText={snippetAnalysesHasMatch}
                hit={hitWithFirstTwoAnalyses as SearchableDecisionInstantSearch}
                query={query}
            />
            {hitAnalysisForDropdown?.analyses && hitAnalysisForDropdown?.analyses.length > 0 && (
                <Accordion
                    onChange={handleAccordionChange}
                    onClick={handleAccordionClick}
                    sx={accordionSx}
                    disableGutters
                >
                    <AccordionSummary
                        expandIcon={<KeyboardArrowDownIcon />}
                        sx={accordionSummarySx}
                    >
                        {isExpanded
                            ? t('decision.accordion.hideAnalysis')
                            : t('decision.accordion.showAnalysis')}
                    </AccordionSummary>
                    <AnalysisList hasFullText={false} hit={hitAnalysisForDropdown} query={query} />
                </Accordion>
            )}
        </>
    );
};

export default DecisionAnalysed;
